
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AdminBill } from "./types";
import { formatDate, formatPrice, getStatusColor } from "./utils";
import { FileText, Mail, MoreVertical, CheckCircle2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useTaxRate } from "@/hooks/useTaxRate";

interface BillsTableProps {
  bills: AdminBill[];
  onUpdateStatus: (billId: string) => void;
  onCancelBill: (billId: string) => void;
  onViewInvoice: (bill: AdminBill) => void;
  disabled?: boolean;
}

export function BillsTable({ 
  bills, 
  onUpdateStatus, 
  onCancelBill, 
  onViewInvoice, 
  disabled = false 
}: BillsTableProps) {
  const canUpdateStatus = (bill: AdminBill) => {
    return false; // Disable the "Check Payment Status" functionality
  };

  const canCancelBill = (bill: AdminBill) => {
    return bill.status.toLowerCase() !== 'cancelled' && 
           bill.status.toLowerCase() !== 'expired' &&
           bill.status.toLowerCase() !== 'paid';
  };
  
  const { taxRate } = useTaxRate();

  const formatDiscountDisplay = (bill: AdminBill) => {
    if (bill.discount <= 0) return formatPrice(0);
    
    let percentageText = '';
    
    // Use the correct percentage value if available
    if (bill.discount_type === 'percentage') {
      // Handle the case where discount_percentage might contain the discount amount
      if (typeof bill.discount_percentage === 'number') {
        if (bill.discount_percentage > 100) {
          // This is likely an error - the discount_percentage contains the amount
          // Calculate the actual percentage
          const originalPrice = bill.plan_price || (bill.plan?.price !== undefined ? bill.plan.price : 0);
          if (originalPrice > 0) {
            const calculatedPercentage = Math.round((bill.discount / originalPrice) * 100);
            percentageText = ` (${calculatedPercentage}%)`;
          }
        } else {
          // Normal case - use the provided percentage
          percentageText = ` (${bill.discount_percentage}%)`;
        }
      } else {
        // Fallback if no discount_percentage provided
        const originalPrice = bill.plan_price || (bill.plan?.price !== undefined ? bill.plan.price : 0);
        if (originalPrice > 0) {
          const calculatedPercentage = Math.round((bill.discount / originalPrice) * 100);
          percentageText = ` (${calculatedPercentage}%)`;
        }
      }
    } else {
      // Calculate percentage for fixed discounts based on plan price
      const originalPrice = bill.plan_price || (bill.plan?.price !== undefined ? bill.plan.price : 0);
      if (originalPrice > 0) {
        const calculatedPercentage = Math.round((bill.discount / originalPrice) * 100);
        percentageText = ` (≈${calculatedPercentage}%)`;
      }
    }
    
    return `${formatPrice(bill.discount)}${percentageText}`;
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Transaction #</TableHead>
          <TableHead>School</TableHead>
          <TableHead>Product</TableHead>
          <TableHead>Plan</TableHead>
          <TableHead>Plan Price</TableHead>
          <TableHead>Tax ({taxRate}%)</TableHead>
          <TableHead>Discount</TableHead>
          <TableHead>Total</TableHead>
          <TableHead>Due Date</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {bills.length === 0 ? (
          <TableRow>
            <TableCell colSpan={11} className="text-center">
              No bills found
            </TableCell>
          </TableRow>
        ) : (
          bills.map((bill) => {
            return (
              <TableRow key={bill.id}>
                <TableCell className="font-medium">{bill.transaction_number}</TableCell>
                <TableCell>{bill.client?.nama_sekolah}</TableCell>
                <TableCell>{bill.plan?.product?.name}</TableCell>
                <TableCell>{bill.plan?.name}</TableCell>
                <TableCell>{bill.plan?.price !== undefined ? formatPrice(bill.plan.price) : '-'}</TableCell>
                <TableCell>{formatPrice(bill.tax)}</TableCell>
                <TableCell>{formatDiscountDisplay(bill)}</TableCell>
                <TableCell className="font-medium">
                  {formatPrice(bill.amount)}
                </TableCell>
                <TableCell>{formatDate(bill.due_date)}</TableCell>
                <TableCell>
                  <Badge className={getStatusColor(bill.status)}>
                    {bill.status.toUpperCase()}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => onViewInvoice(bill)}
                      className="flex items-center gap-1"
                    >
                      <FileText className="h-4 w-4" />
                      Invoice
                    </Button>
                  
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" disabled={disabled}>
                          <MoreVertical className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {/* Keep the menu item but make it disabled */}
                        <DropdownMenuItem 
                          onClick={() => onUpdateStatus(bill.id)}
                          disabled={true}
                        >
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          <span>Check Payment Status</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
